<template>
  <div :key="result" class="calculation-steps" :class="{ result: result }">
    <h2 v-if="!result" class="h4">
      Calculation Example:
    </h2>
    <h2 v-else class="h4">
      Calculation Steps:
    </h2>
    <p>There are six steps you would follow in hypothesis testing:</p>
    <ol>
      <li v-if="!result">
        <p>Formulate the null and alternative hypotheses in three different ways:</p>
        <p class="formulas">
          \[H_0:\theta=\theta_0\ versus\ H_1:\theta\neq\theta_0\]
        </p>
        <p class="formulas">
          \[H_0:\theta\le\theta_0\ versus\ H_1:\theta>\theta_0\]
        </p>
        <p class="formulas">
          \[H_0:\theta\geq\theta_0\ versus\ H_1:\theta&lt;\theta_0\]
        </p>
      </li>
      <li v-else>
        State the hypothesis:
      </li>
      <p v-if="result" class="formulas">
        \[H_0:μ_0{{ calculationData.selectedOperator }}{{ calculationData.nullHypothesis }}\]
      </p>
      <p v-if="result">
        And the alternative hypothesis:
      </p>
      <p v-if="result" class="formulas">
        \[H_1:μ_0{{ calculationData.alternateHypothesisOperator }}{{ calculationData.nullHypothesis }}\]
      </p>
      <p v-if="!result">
        If there’s a possibility that the alternative hypothesis indicates a difference in both directions (greater than and less than the null hypothesis value), we conduct a <span class="bolded-texts">two-sided test</span>. On the other hand, if it specifies a difference in only one direction (either greater than or less than the null hypothesis value), we say this is a <span class="bolded-texts">one-tailed test</span>.
      </p>
      <div v-else>
        <p v-if="calculationData.selectedOperator === '='">
          Since the alternative hypothesis includes the possibility of a difference in both directions (greater than and less than the null hypothesis value), this is a <span class="bolded-texts">two-sided test</span>.
        </p>
        <p v-else>
          Since the alternative hypothesis <span class="inline-formulas">\[H_1\]</span> specifies a difference in only one direction (either greater than or less than the null hypothesis value), we say this is a <span class="bolded-texts">one-tailed test</span>.
        </p>
      </div>
      <li>Select an appropriate test for your hypothesis:</li>
      <p v-if="!result">
        If the population variance is <span class="bolded-texts">known</span> and the sample size is <span class="bolded-texts">greater than or equal to 30</span>, the appropriate test statistic is the <span class="bolded-texts">z-test</span>. When the population variance is <span class="bolded-texts">unknown</span> or the sample size is <span class="bolded-texts">less than 30</span>, the appropriate test statistic is the <span class="bolded-texts">t-test</span>.
      </p>
      <div v-else>
        <p v-if="calculationData.answer === 'Yes' && calculationData.size >= 30">
          Since the population variance is <span class="bolded-texts">known</span> and the sample size is <span class="bolded-texts">greater than or equal to 30</span>, the appropriate test statistic is the <span class="bolded-texts">z-test</span>.
        </p>
        <p v-else-if="calculationData.answer === 'Yes' && calculationData.size < 30">
          Since the population variance is <span class="bolded-texts">known</span> but the sample size is <span class="bolded-texts">lower than 30</span>, the appropriate test statistic is the <span class="bolded-texts">t-test</span>.
        </p>
        <p v-else>
          Since the population variance is <span class="bolded-texts">unknown</span> and the sample size is <span v-if="calculationData.size < 30" class="bolded-texts">lower than 30</span><span v-else class="bolded-texts">greater than or equal to 30</span>, the appropriate test statistic is the <span class="bolded-texts">t-test</span>.
        </p>
      </div>
      <li>Specify the level of significance (α):</li>
      <p class="formulas">
        \[\alpha=1-Confidence\ Level\]
      </p>
      <li>Determine the critical values and state the decision rule:</li>
      <p v-if="!result">
        Consult the <span class="bolded-texts">standard normal distribution</span> table when using the z-test or the <span class="bolded-texts">Student’s t table</span> when using the t-test. Critical values are defined based on the significance level (α) and the type of test you're conducting (one-tailed or two-tailed):
      </p>
      <p v-else v-html="currentTailData[1]" />
      <p v-if="!result" class="top-margins">
        If this is a <span class="bolded-texts">two-sided test</span>, divide the significance level (α) equally between the two tails:
      </p>
      <div v-if="!result">
        <p class="formulas">
          \[(p) = 1 – α/2\]
        </p>
        <p class="formulas">
          \[z_{0.025} = 1.96\]
        </p>
      </div>
      <div v-else>
        <div v-if="calculationData.selectedOperator === '='">
          <p class="formulas">
            \[(p) = 1 – {{ calculationData.significanceLevel }}/2\]
          </p>
          <p class="formulas">
            \[{{ currentStatistic[0] }}_{ {{ significanceLevelDividedByTwo }} } = {{ calculationData.currentScore }}\]
          </p>
        </div>
        <div v-else>
          <p class="formulas">
            \[(p) = 1 – {{ calculationData.significanceLevel }}\]
          </p>
          <p class="formulas">
            \[{{ currentStatistic[0] }}_{ {{ calculationData.significanceLevel }} } = {{ calculationData.currentScore }}\]
          </p>
        </div>
      </div>
      <p v-if="!result">
        If this is a <span class="bolded-texts">one-sided test</span>, allocate the significance level (α) to the left or right tail of the distribution:
      </p>
      <p v-if="!result" class="formulas">
        \[z_{0.05} = 1.645\]
      </p>
      <ul v-if="!result">
        <li class="no-margin">
          The critical values for a <span class="bolded-texts">two-sided test</span> are:
        </li>
      </ul>
      <p v-else>
        The critical value<span v-if="calculationData.selectedOperator === '='">s</span> for a <span class="bolded-texts">{{ currentTailData[0] }} test</span> <span v-if="calculationData.selectedOperator === '='">are</span><span v-else>is</span>
      </p>
      <p v-if="!result" class="formulas">
        \[−z_{α/2} = −1.96\ and\ z_{α/2} = 1.96\]
      </p>
      <div v-else>
        <p v-if="currentTailData[0] === 'two sided'" class="formulas">
          \[−{{ currentStatistic[0] }}_{α/2} = −{{ calculationData.currentScore }}\ and\ {{ currentStatistic[0] }}_{α/2} = {{ calculationData.currentScore }}\]
        </p>
        <p v-else-if="currentTailData[0] === 'left tailed'" class="formulas">
          \[−{{ currentStatistic[0] }}_α = −{{ calculationData.currentScore }}\]
        </p>
        <p v-else class="formulas">
          \[{{ currentStatistic[0] }}_α = {{ calculationData.currentScore }}\]
        </p>
      </div>
      <p>The decision rule of the hypothesis test is:</p>

      <div v-if="!result">
        <p class="formulas">
          \[If\ Z ≤ −z_{0.025}\ or\ Z ≥ z_{0.025},\ reject\ H_0\]
        </p>
        <p class="formulas">
          \[If\ Z &gt; −z_{0.025}\ or\ Z &lt; z_{0.025},\ fail\ to\ reject\ H_0\]
        </p>
      </div>

      <div v-else>
        <div v-if="calculationData.selectedOperator === '='">
          <p class="formulas">
            \[If\ {{ currentStatistic[1] }} ≤ −{{ currentStatistic[0] }}_{ {{ significanceLevelDividedByTwo }} }\ or\ {{ currentStatistic[1] }} ≥ {{ currentStatistic[0] }}_{ {{ significanceLevelDividedByTwo }} },\ reject\ H_0\]
          </p>
          <p class="formulas">
            \[If\ {{ currentStatistic[1] }} &gt; −{{ currentStatistic[0] }}_{ {{ significanceLevelDividedByTwo }} }\ or\ {{ currentStatistic[1] }} &lt; {{ currentStatistic[0] }}_{ {{ significanceLevelDividedByTwo }} },\ fail\ to\ reject\ H_0\]
          </p>
        </div>
        <div v-else-if="calculationData.selectedOperator === '≥'">
          <p class="formulas">
            \[If\ {{ currentStatistic[1] }}\le{-{{ currentStatistic[0] }} }_{ {{ calculationData.significanceLevel }} }\ ,\ reject\ H_0\]
          </p>
          <p class="formulas">
            \[If\ {{ currentStatistic[1] }}>-{{ currentStatistic[0] }}_{ {{ calculationData.significanceLevel }} }\ ,\ fail\ to\ reject\ H_0\]
          </p>
        </div>
        <div v-else>
          <p class="formulas">
            \[If\ {{ currentStatistic[1] }}\ge{ {{ currentStatistic[0] }} }_{ {{ calculationData.significanceLevel }} }\ ,\ reject\ H_0\]
          </p>
          <p class="formulas">
            \[If\ {{ currentStatistic[1] }}&lt;{{ currentStatistic[0] }}_{ {{ calculationData.significanceLevel }} }\ ,\ fail\ to\ reject\ H_0\]
          </p>
        </div>
      </div>

      <p class="top-margin-mobile">
        The decision rule (based on p-value approach) is:
      </p>
      <p class="formulas">
        \[p−value ≤ α,\ Reject\ H_0\]
      </p>
      <p class="formulas">
        \[p−value > α,\ Fail\ to\ reject\ H_0\]
      </p>
      <div v-if="!result">
        <ul>
          <li class="no-margin">
            The critical values for a <span class="bolded-texts">left-tailed test</span> is:
          </li>
        </ul>
        <p class="formulas">
          \[−z_{0.05} = −1.645\]
        </p>
        <p>The decision rule (based on critical value approach) is:</p>
        <p class="formulas">
          \[If\ Z\ ≤ −z_{0.05},\ reject\ H_0\]
        </p>
        <p class="formulas">
          \[If\ Z\ > −z_{0.05},\ Fail\ to\ reject\ H_0\]
        </p>
        <p>The decision rule (based on p-value approach) is:</p>
        <p class="formulas">
          \[p−value ≤ α,\ Reject\ H_0\]
        </p>
        <p class="formulas">
          \[p−value > α,\ Fail\ to\ reject\ H_0\]
        </p>
        <ul>
          <li class="no-margin">
            The critical values for a <span class="bolded-texts">right-tailed test</span> is:
          </li>
        </ul>
        <p class="formulas">
          \[z_{0.05} = 1.645\]
        </p>
        <p>The decision rule (based on critical value approach) is:</p>
        <p class="formulas">
          \[If\ Z\ > −z_{0.05},\ reject\ H_0\]
        </p>
        <p class="formulas">
          \[If\ Z\ ≤ −z_{0.05},\ Fail\ to\ reject\ H_0\]
        </p>
        <p class="top-margin-mobile">
          The decision rule (based on p-value approach) is:
        </p>
        <p class="formulas">
          \[p−value ≤ α,\ Reject\ H_0\]
        </p>
        <p class="formulas">
          \[p−value > α,\ Fail\ to\ reject\ H_0\]
        </p>
      </div>
      <li>Calculate the test statistic:</li>
      <div v-if="!result">
        <p class="formulas">
          \[Z=\frac{\bar{x}-\mu_0}{\sigma/\sqrt n}\]
        </p>
        <p>or</p>
        <p class="formulas">
          \[T=\frac{\bar{x}-\mu_0}{s/\sqrt n}\]
        </p>
      </div>
      <p v-else class="formulas">
        \[{{ currentStatistic[1] }}=\frac{\bar{x}-\mu_0}{ {{ currentStatistic[2] }}/\sqrt n} = \frac{ {{ calculationData.mean }}-{{ calculationData.nullHypothesis }}}{ {{ calculationData.standardDeviation }}/\sqrt{ {{ calculationData.size }}} } = {{ calculationData.testStatistic }}\]
      </p>
      <li>Make a decision based on the result:</li>
      <ul>
        <li class="top-margins">
          Critical Value Approach:
        </li>
        <div v-if="!result">
          <p class="formulas">
            \[If\ z\ ≤ −1.96\ or\ z\ ≥ 1.96,\ then\ reject\ H_0\ (Two−tailed)\]
          </p>
          <p class="formulas">
            \[If\ z\ ≤ −1.645,\ then\ reject\ H_0\ (Left−tailed)\]
          </p>
          <p class="formulas">
            \[If\ z\ ≥ 1.645,\ then\ reject\ H_0\ (Right−tailed)\]
          </p>
        </div>
        <div v-else>
          <p v-if="calculationData.selectedOperator === '='" class="formulas">
            \[If\ {{ calculationData.testStatistic }}\ ≤ -{{ calculationData.currentScore }}\ or\ {{ calculationData.testStatistic }}\ ≥ {{ calculationData.currentScore }},\ then\ reject\ H_0\]
          </p>
          <p v-else-if="calculationData.selectedOperator === '≤'" class="formulas">
            \[If\ {{ calculationData.testStatistic }}\ ≥ {{ calculationData.currentScore }},\ then\ reject\ H_0\]
          </p>
          <p v-else class="formulas">
            \[If\ {{ calculationData.testStatistic }}\ ≤ -{{ calculationData.currentScore }},\ then\ reject\ H_0\]
          </p>
        </div>
        <div v-if="result" class="top-margin-mobile">
          <p v-if="testStatisticCriticalValueComparison()">
            <span class="bolded-texts">Reject <span class="inline-formulas">\[H_0\]</span></span> since the calculated <span class="bolded-texts">{{ currentStatistic[0] }}-statistic</span> is {{ lowerOrHigher }} than the critical value.
          </p>
          <div v-else>
            <p v-if="calculationData.selectedOperator === '='">
              <span class="bolded-texts">Fail to reject</span> <span class="inline-formulas">\[H_0\]</span> because the calculated <span class="bolded-texts">{{ currentStatistic[0] }}-statistic</span> falls between the two critical values.
            </p>
            <p v-else-if="calculationData.selectedOperator === '≥'">
              <span class="bolded-texts">Fail to reject</span> <span class="inline-formulas">\[H_0\]</span> because the calculated <span class="bolded-texts">{{ currentStatistic[0] }}-statistic</span> is higher than the critical value.
            </p>
            <p v-else>
              <span class="bolded-texts">Fail to reject</span> <span class="inline-formulas">\[H_0\]</span> because the calculated <span class="bolded-texts">{{ currentStatistic[0] }}-statistic</span> is lower than the critical value.
            </p>
          </div>
        </div>
        <li class="top-margin-mobile" :class="{ 'no-margin': !result, 'p-value-approach': result }">
          P-value Approach:
        </li>
        <div v-if="!result">
          <p class="formulas">
            \[p−value ≤ α,\ Reject\ H_0\]
          </p>
          <p class="formulas">
            \[p−value > α,\ Fail\ to\ reject\ H_0\]
          </p>
        </div>
        <div v-else>
          <p class="formulas">
            \[p−value = {{ calculationData.pValue }}\]
          </p>
          <div>
            <p v-if="pValueSignificanceLevelComparison !== '>'" class="formulas">
              \[p−value {{ pValueSignificanceLevelComparison }} {{ calculationData.significanceLevel }},\ Reject\ H_0\]
            </p>
            <p v-else class="formulas">
              \[p−value {{ pValueSignificanceLevelComparison }} {{ calculationData.significanceLevel }},\ Fail\ to\ reject\ H_0\]
            </p>
          </div>
        </div>
        <div v-if="result">
          <p v-if="pValueSignificanceLevelComparison !== '>'">
            Conclusion: <span class="bolded-texts">Reject <span class="inline-formulas">\[H_0\]</span></span> because the p-value is lower than the specified significance level (α).
          </p>
          <p v-else>
            Conclusion: <span class="bolded-texts">Fail to reject <span class="inline-formulas">\[H_0\]</span></span> because the p-value is higher than the specified significance level (α).
          </p>
        </div>
      </ul>
    </ol>

    <!-- Python -->
    <div v-if="result" class="python-r-codes python">
      <span class="img-text"><img alt="Python icon" src="../../../../assets/images/icon-python-calc.png"> <h4 class="h6">Your Result in Python:</h4></span>
      <p class="codes-info">
        The following Python code should provide the hypothesis testing using the input data you entered.
      </p>
      <div class="codes-container">
        <div class="codes">
          <!-- Yes --------------------------------------------------------------------------------------------------- -->

          <div v-if="calculationData.answer === 'Yes' && calculationData.size >= 30">
            <!-- two tails -->
            <pre
              v-if="calculationData.method === 'readyData' && calculationData.selectedOperator === '='"
              class="language-python"
            ><code id="python">
import numpy as np

from scipy.stats import norm

# Null hypothesis: mean = ${{ calculationData.nullHypothesis }}

null_mean = {{ calculationData.nullHypothesis }}

# Population standard deviation: σ = ${{ calculationData.standardDeviation }}

pop_std_dev = {{ calculationData.standardDeviation }}

# Sample size

n = {{ calculationData.size }}

# Known sample mean

sample_mean = {{ calculationData.mean }}

# Calculate the standard error of the mean

se = pop_std_dev / np.sqrt(n)

# Calculate the z-score

z_score = (sample_mean - null_mean) / se

# Set the significance level (α)

significance_level = {{ calculationData.significanceLevel }}

# Determine the critical value

critical_value = norm.ppf(1 - significance_level/2)

# Printing the z-score and critical value

print("Z-Score:", round(z_score, 4))

print("Critical Value (Two-tailed): ±", round(critical_value, 4))

# Perform the two-tailed hypothesis test

if np.abs(z_score) &gt; critical_value:

    print("Reject the null hypothesis")

else:

    print("Fail to reject the null hypothesis")</code></pre>
            <pre
              v-if="calculationData.method === 'rawData' && calculationData.selectedOperator === '='"
              class="language-python"
            ><code id="python">
import numpy as np

from scipy.stats import norm

# Given dataset

dataset = np.array(<span class="no-wrap">{{ inputToNumbers[0] }}</span>)

# Null hypothesis: mean = ${{ calculationData.nullHypothesis }}

null_mean = {{ calculationData.nullHypothesis }}

# Population standard deviation: σ = ${{ calculationData.standardDeviation }}

pop_std_dev = {{ calculationData.standardDeviation }}

# Sample size

n = len(dataset)

# Sample mean

sample_mean = np.mean(dataset)

# Calculate the standard error of the mean

se = pop_std_dev / np.sqrt(n)

# Calculate the z-score

z_score = (sample_mean - null_mean) / se

# Set the significance level (α)

significance_level = {{ calculationData.significanceLevel }}

# Determine the critical value

critical_value = norm.ppf(1 - significance_level/2)

# Printing the z-score and critical value

print("Z-Score:", round(z_score, 4))

print("Critical Value (Two-tailed): ±", round(critical_value, 4))

# Perform the two-tailed hypothesis test

if np.abs(z_score) &gt; critical_value:

    print("Reject the null hypothesis")

else:

    print("Fail to reject the null hypothesis")</code></pre>

            <!-- left tail -->
            <pre
              v-if="calculationData.method === 'readyData' && calculationData.selectedOperator === '≥'"
              class="language-python"
            ><code id="python">
import numpy as np

from scipy.stats import norm

# Null hypothesis: mean = ${{ calculationData.nullHypothesis }}

null_mean = {{ calculationData.nullHypothesis }}

# Population standard deviation: σ = ${{ calculationData.standardDeviation }}

pop_std_dev = {{ calculationData.standardDeviation }}

# Sample size

n = {{ calculationData.size }}

# Known sample mean

sample_mean = {{ calculationData.mean }}

# Calculate the standard error of the mean

se = pop_std_dev / np.sqrt(n)

# Calculate the z-score

z_score = (sample_mean - null_mean) / se

# Set the significance level (α)

significance_level = {{ calculationData.significanceLevel }}

# Determine the critical value

critical_value = norm.ppf(significance_level)

# Printing the z-score and critical value

print("Z-Score:", round(z_score, 4))

print("Critical Value (Left-tailed):", round(critical_value, 4))

# Perform the one-tailed hypothesis test

if z_score &lt; critical_value:

    print("Reject the null hypothesis")

else:

    print("Fail to reject the null hypothesis")</code></pre>
            <pre
              v-if="calculationData.method === 'rawData' && calculationData.selectedOperator === '≥'"
              class="language-python"
            ><code id="python">
import numpy as np

from scipy.stats import norm

# Given dataset

dataset = np.array(<span class="no-wrap">{{ inputToNumbers[0] }}</span>)

# Null hypothesis: mean = ${{ calculationData.nullHypothesis }}

null_mean = {{ calculationData.nullHypothesis }}

# Population standard deviation: σ = ${{ calculationData.standardDeviation }}

pop_std_dev = {{ calculationData.standardDeviation }}

# Sample size

n = len(dataset)

# Sample mean

sample_mean = np.mean(dataset)

# Calculate the standard error of the mean

se = pop_std_dev / np.sqrt(n)

# Calculate the z-score

z_score = (sample_mean - null_mean) / se

# Set the significance level (α)

significance_level = {{ calculationData.significanceLevel }}

# Determine the critical value

critical_value = norm.ppf(significance_level)

# Printing the z-score and critical value

print("Z-Score:", round(z_score, 4))

print("Critical Value (Left-tailed):", round(critical_value, 4))

# Perform the one-tailed hypothesis test

if z_score &lt; critical_value:

    print("Reject the null hypothesis")

else:

    print("Fail to reject the null hypothesis")</code></pre>

            <!-- right tail -->
            <pre
              v-if="calculationData.method === 'readyData' && calculationData.selectedOperator === '≤'"
              class="language-python"
            ><code id="python">
import numpy as np

from scipy.stats import norm

# Null hypothesis: mean ≥ ${{ calculationData.nullHypothesis }}

null_mean = {{ calculationData.nullHypothesis }}

# Population standard deviation: σ = ${{ calculationData.standardDeviation }}

pop_std_dev = {{ calculationData.standardDeviation }}

# Sample size

n = {{ calculationData.size }}

# Known sample mean

sample_mean = {{ calculationData.mean }}

# Calculate the standard error of the mean

se = pop_std_dev / np.sqrt(n)

# Calculate the z-score

z_score = (sample_mean - null_mean) / se

# Set the significance level (α)

significance_level = {{ calculationData.significanceLevel }}

# Determine the critical value

critical_value = norm.ppf(1 - significance_level)

# Printing the z-score and critical value

print("Z-Score:", round(z_score, 4))

print("Critical Value (Right-tailed):", round(critical_value, 4))

# Perform the one-tailed hypothesis test

if z_score &gt; critical_value:

    print("Reject the null hypothesis")

else:

    print("Fail to reject the null hypothesis")</code></pre>
            <pre
              v-if="calculationData.method === 'rawData' && calculationData.selectedOperator === '≤'"
              class="language-python"
            ><code id="python">
import numpy as np

from scipy.stats import norm

# Given dataset

dataset = np.array(<span class="no-wrap">{{ inputToNumbers[0] }}</span>)

# Null hypothesis: mean ≥ ${{ calculationData.nullHypothesis }}

null_mean = {{ calculationData.nullHypothesis }}

# Population standard deviation: σ = ${{ calculationData.standardDeviation }}

pop_std_dev = {{ calculationData.standardDeviation }}

# Sample size

n = len(dataset)

# Sample mean

sample_mean = np.mean(dataset)

# Calculate the standard error of the mean

se = pop_std_dev / np.sqrt(n)

# Calculate the z-score

z_score = (sample_mean - null_mean) / se

# Set the significance level (α)

significance_level = {{ calculationData.significanceLevel }}

# Determine the critical value

critical_value = norm.ppf(1 - significance_level)

# Printing the z-score and critical value

print("Z-Score:", round(z_score, 4))

print("Critical Value (Right-tailed):", round(critical_value, 4))

# Perform the one-tailed hypothesis test

if z_score &gt; critical_value:

    print("Reject the null hypothesis")

else:

    print("Fail to reject the null hypothesis")</code></pre>
          </div>

          <div v-else-if="calculationData.answer === 'Yes' && calculationData.size < 30">
            <!-- two tails -->
            <pre
              v-if="calculationData.method === 'readyData' && calculationData.selectedOperator === '='"
              class="language-python"
            ><code id="python">
import numpy as np

from scipy.stats import t

# Null hypothesis mean

null_mean = {{ calculationData.nullHypothesis }}

# Known population standard deviation

population_std_dev = {{ calculationData.standardDeviation }}

# Sample size

n = {{ calculationData.size }}

# Sample mean

sample_mean = {{ calculationData.mean }}

# Calculate the t-statistic

t_statistic = (sample_mean - null_mean) / (population_std_dev / np.sqrt(n))

# Degrees of freedom

df = n - 1

# Calculate the critical values at a given alpha level

# Significance level

alpha = {{ calculationData.significanceLevel }}

critical_value_lower = t.ppf(alpha / 2, df)

critical_value_upper = t.ppf(1 - alpha / 2, df)

# Printing the t-statistic and critical values

print("T-Statistic:", round(t_statistic, 4))

print("Critical Values (Two-tailed):", round(critical_value_lower, 4), "and", round(critical_value_upper, 4))

# Make a decision based on the t-statistic and critical values

if t_statistic &lt; critical_value_lower or t_statistic > critical_value_upper:

    print("Reject the null hypothesis")

else:

    print("Fail to reject the null hypothesis")</code></pre>
            <pre
              v-if="calculationData.method === 'rawData' && calculationData.selectedOperator === '='"
              class="language-python"
            ><code id="python">
import numpy as np

from scipy.stats import t

# Sample data

dataset = np.array(<span class="no-wrap">{{ inputToNumbers[0] }}</span>)

# Null hypothesis mean

null_mean = {{ calculationData.nullHypothesis }}

# Known population standard deviation

population_std_dev = {{ calculationData.standardDeviation }}

# Sample size

n = len(data)

# Sample mean

sample_mean = np.mean(data)

# Calculate the t-statistic

t_statistic = (sample_mean - null_mean) / (population_std_dev / np.sqrt(n))

# Degrees of freedom

df = n - 1

# Calculate the critical values at a given alpha level

# Significance level

alpha = {{ calculationData.significanceLevel }}

critical_value_lower = t.ppf(alpha / 2, df)

critical_value_upper = t.ppf(1 - alpha / 2, df)

# Printing the t-statistic and critical values

print("T-Statistic:", round(t_statistic, 4))

print("Critical Values (Two-tailed):", round(critical_value_lower, 4), "and", round(critical_value_upper, 4))

# Make a decision based on the t-statistic and critical values

if t_statistic &lt; critical_value_lower or t_statistic > critical_value_upper:

    print("Reject the null hypothesis")

else:

    print("Fail to reject the null hypothesis")</code></pre>

            <!-- left tail -->
            <pre
              v-if="calculationData.method === 'readyData' && calculationData.selectedOperator === '≥'"
              class="language-python"
            ><code id="python">
import numpy as np

from scipy.stats import t

# Null hypothesis mean

null_mean = {{ calculationData.nullHypothesis }}

# Known population standard deviation

population_std_dev = {{ calculationData.standardDeviation }}

# Sample size

n = {{ calculationData.size }}

# Sample mean

sample_mean = {{ calculationData.mean }}

# Calculate the t-statistic

t_statistic = (sample_mean - null_mean) / (population_std_dev / np.sqrt(n))

# Degrees of freedom

df = n - 1

# Calculate the critical value at a given alpha level (left-tailed)

# Significance level

alpha = {{ calculationData.significanceLevel }}

critical_value = t.ppf(alpha, df)

# Printing the results

print("T-Statistic:", round(t_statistic, 4))

print("Critical Value (Left-tailed):", round(critical_value, 4))

# Make a decision based on the t-statistic and critical value

if t_statistic &lt; critical_value:

    print("Reject the null hypothesis")

else:

    print("Fail to reject the null hypothesis")</code></pre>
            <pre
              v-if="calculationData.method === 'rawData' && calculationData.selectedOperator === '≥'"
              class="language-python"
            ><code id="python">
import numpy as np

from scipy.stats import t

# Sample data

dataset = np.array(<span class="no-wrap">{{ inputToNumbers[0] }}</span>)

# Null hypothesis mean

null_mean = {{ calculationData.nullHypothesis }}

# Known population standard deviation

population_std_dev = {{ calculationData.standardDeviation }}

# Sample size

n = len(data)

# Sample mean

sample_mean = np.mean(data)

# Calculate the t-statistic

t_statistic = (sample_mean - null_mean) / (population_std_dev / np.sqrt(n))

# Degrees of freedom

df = n - 1

# Calculate the critical value at a given alpha level (left-tailed)

# Significance level

alpha = {{ calculationData.significanceLevel }}

critical_value = t.ppf(alpha, df)

# Printing the results

print("T-Statistic:", round(t_statistic, 4))

print("Critical Value (Left-tailed):", round(critical_value, 4))

# Make a decision based on the t-statistic and critical value

if t_statistic &lt; critical_value:

    print("Reject the null hypothesis")

else:

    print("Fail to reject the null hypothesis")</code></pre>

            <!-- right tail -->
            <pre
              v-if="calculationData.method === 'readyData' && calculationData.selectedOperator === '≤'"
              class="language-python"
            ><code id="python">
import numpy as np

from scipy.stats import t

# Null hypothesis mean

null_mean = {{ calculationData.nullHypothesis }}

# Known population standard deviation

population_std_dev = {{ calculationData.standardDeviation }}

# Sample size

n = {{ calculationData.size }}

# Sample mean

sample_mean = {{ calculationData.mean }}

# Calculate the t-statistic

t_statistic = (sample_mean - null_mean) / (population_std_dev / np.sqrt(n))

# Degrees of freedom

df = n - 1

# Calculate the critical value at a given alpha level (right-tailed)

# Significance level

alpha = {{ calculationData.significanceLevel }}

critical_value = t.ppf(1 - alpha, df)

# Printing the results

print("T-Statistic:", round(t_statistic, 4))

print("Critical Value (Right-tailed):", round(critical_value, 4))

# Make a decision based on the t-statistic and critical value

if t_statistic > critical_value:

    print("Reject the null hypothesis")

else:

    print("Fail to reject the null hypothesis")
              </code></pre>
            <pre
              v-if="calculationData.method === 'rawData' && calculationData.selectedOperator === '≤'"
              class="language-python"
            ><code id="python">
import numpy as np

from scipy.stats import t

# Sample data

dataset = np.array(<span class="no-wrap">{{ inputToNumbers[0] }}</span>)

# Null hypothesis mean

null_mean = {{ calculationData.nullHypothesis }}

# Known population standard deviation

population_std_dev = {{ calculationData.standardDeviation }}

# Sample size

n = len(data)

# Sample mean

sample_mean = np.mean(data)

# Calculate the t-statistic

t_statistic = (sample_mean - null_mean) / (population_std_dev / np.sqrt(n))

# Degrees of freedom

df = n - 1

# Calculate the critical value at a given alpha level (right-tailed)

# Significance level

alpha = {{ calculationData.significanceLevel }}

critical_value = t.ppf(1 - alpha, df)

# Printing the results

print("T-Statistic:", round(t_statistic, 4))

print("Critical Value (Right-tailed):", round(critical_value, 4))

# Make a decision based on the t-statistic and critical value

if t_statistic > critical_value:

    print("Reject the null hypothesis")

else:

    print("Fail to reject the null hypothesis")</code></pre>
          </div>

          <!-- No ------------------------------------------------------------------------------------------------------- -->

          <!-- two tails -->
          <pre
            v-if="calculationData.method === 'readyData' && calculationData.answer === 'No' && calculationData.selectedOperator === '='"
            class="language-python"
          ><code id="python">
import numpy as np

from scipy.stats import t

# Null hypothesis: mean = ${{ calculationData.nullHypothesis }}

null_mean = {{ calculationData.nullHypothesis }}

# Sample size

n = {{ calculationData.size }}

# Sample mean

sample_mean = {{ calculationData.mean }}

# Sample standard deviation

sample_std_dev = {{ calculationData.standardDeviation }}

# Calculate the standard error of the mean

se = sample_std_dev / np.sqrt(n)

# Calculate the t-score

t_score = (sample_mean - null_mean) / se

# Set the significance level (α)

significance_level = {{ calculationData.significanceLevel }}

# Degrees of freedom

df = n - 1

# Determine the critical value

critical_value = t.ppf(1 - significance_level/2, df)

# Printing the t-score and critical value

print("T-Score:", round(t_score, 4))

print("Critical Value (Two-tailed): ±", round(critical_value, 4))

# Perform the hypothesis test

if np.abs(t_score) > critical_value:

    print("Reject the null hypothesis")

else:

    print("Fail to reject the null hypothesis")</code></pre>
          <pre
            v-if="calculationData.method === 'rawData' && calculationData.answer === 'No' && calculationData.selectedOperator === '='"
            class="language-python"
          ><code id="python">
import numpy as np

from scipy.stats import t

dataset = np.array(<span class="no-wrap">{{ inputToNumbers[0] }}</span>)

# Null hypothesis: mean = ${{ calculationData.nullHypothesis }}

null_mean = {{ calculationData.nullHypothesis }}

# Sample size

n = {{ calculationData.size }}

# Sample mean

sample_mean = {{ calculationData.mean }}

# Sample standard deviation

sample_std_dev = np.std(dataset, ddof=1)

# Calculate the standard error of the mean

se = sample_std_dev / np.sqrt(n)

# Calculate the t-score

t_score = (sample_mean - null_mean) / se

# Set the significance level (α)

significance_level = {{ calculationData.significanceLevel }}

# Degrees of freedom

df = n - 1

# Determine the critical value

critical_value = t.ppf(1 - significance_level/2, df)

# Printing the t-score and critical value

print("T-Score:", round(t_score, 4))

print("Critical Value (Two-tailed): ±", round(critical_value, 4))

# Perform the hypothesis test

if np.abs(t_score) > critical_value:

    print("Reject the null hypothesis")

else:

    print("Fail to reject the null hypothesis")</code></pre>

          <!-- left tail -->
          <pre
            v-if="calculationData.method === 'readyData' && calculationData.answer === 'No' && calculationData.selectedOperator === '≥'"
            class="language-python"
          ><code id="python">
import numpy as np

from scipy.stats import t

# Given dataset

# Null hypothesis: mean = ${{ calculationData.nullHypothesis }}

null_mean = {{ calculationData.nullHypothesis }}

# Sample size

n = {{ calculationData.size }}

# Known sample mean

sample_mean = {{ calculationData.mean }}

# Sample standard deviation

sample_std_dev = {{ calculationData.standardDeviation }}

# Calculate the standard error of the mean

se = sample_std_dev / np.sqrt(n)

# Calculate the t-score

t_score = (sample_mean - null_mean) / se

# Set the significance level (α)

significance_level = {{ calculationData.significanceLevel }}

# Degrees of freedom

df = n - 1

# Determine the critical value

critical_value = t.ppf(significance_level, df)

# Printing the t-score and critical value

print("T-Score:", round(t_score, 4))

print("Critical Value (Left-tailed):", round(critical_value, 4))

# Perform the hypothesis test

if t_score &lt; critical_value:

    print("Reject the null hypothesis")

else:

    print("Fail to reject the null hypothesis")</code></pre>
          <pre
            v-if="calculationData.method === 'rawData' && calculationData.answer === 'No' && calculationData.selectedOperator === '≥'"
            class="language-python"
          ><code id="python">
import numpy as np

from scipy.stats import t

# Given dataset

dataset = np.array(<span class="no-wrap">{{ inputToNumbers[0] }}</span>)

# Null hypothesis: mean = ${{ calculationData.nullHypothesis }}

null_mean = {{ calculationData.nullHypothesis }}

# Sample size

n = len(dataset)

# Sample mean

sample_mean = np.mean(dataset)

# Sample standard deviation

sample_std_dev = np.std(dataset, ddof=1)

# Calculate the standard error of the mean

se = sample_std_dev / np.sqrt(n)

# Calculate the t-score

t_score = (sample_mean - null_mean) / se

# Set the significance level (α)

significance_level = {{ calculationData.significanceLevel }}

# Degrees of freedom

df = n - 1

# Determine the critical value

critical_value = t.ppf(significance_level, df)

# Printing the t-score and critical value

print("T-Score:", round(t_score, 4))

print("Critical Value (Left-tailed):", round(critical_value, 4))

# Perform the hypothesis test

if t_score &lt; critical_value:

    print("Reject the null hypothesis")

else:

    print("Fail to reject the null hypothesis")</code></pre>

          <!-- right tail -->
          <pre
            v-if="calculationData.method === 'readyData' && calculationData.answer === 'No' && calculationData.selectedOperator === '≤'"
            class="language-python"
          ><code id="python">
import numpy as np

from scipy.stats import t

# Null hypothesis: mean ≥ ${{ calculationData.nullHypothesis }}

null_mean = {{ calculationData.nullHypothesis }}

# Sample size

n = {{ calculationData.size }}

# Known sample mean

sample_mean = {{ calculationData.mean }}

# Sample standard deviation

sample_std_dev = {{ calculationData.standardDeviation }}

# Calculate the standard error of the mean

se = sample_std_dev / np.sqrt(n)

# Calculate the t-score

t_score = (sample_mean - null_mean) / se

# Set the significance level (α)

significance_level = {{ calculationData.significanceLevel }}

# Degrees of freedom

df = n - 1

# Determine the critical value

critical_value = t.ppf(1 - significance_level, df)

# Printing the t-score and critical value

print("T-Score:", round(t_score, 4))

print("Critical Value (Right-tailed):", round(critical_value, 4))

# Perform the hypothesis test

if t_score > critical_value:

    print("Reject the null hypothesis")

else:

    print("Fail to reject the null hypothesis")</code></pre>
          <pre
            v-if="calculationData.method === 'rawData' && calculationData.answer === 'No' && calculationData.selectedOperator === '≤'"
            class="language-python"
          ><code id="python">
import numpy as np

from scipy.stats import t

dataset = np.array(<span class="no-wrap">{{ inputToNumbers[0] }}</span>)

# Null hypothesis: mean ≥ ${{ calculationData.nullHypothesis }}

null_mean = {{ calculationData.nullHypothesis }}

# Sample size

n = len(dataset)

# Sample mean

sample_mean = np.mean(dataset)

# Sample standard deviation

sample_std_dev = np.std(dataset, ddof=1)

# Calculate the standard error of the mean

se = sample_std_dev / np.sqrt(n)

# Calculate the t-score

t_score = (sample_mean - null_mean) / se

# Set the significance level (α)

significance_level = {{ calculationData.significanceLevel }}

# Degrees of freedom

df = n - 1

# Determine the critical value

critical_value = t.ppf(1 - significance_level, df)

# Printing the t-score and critical value

print("T-Score:", round(t_score, 4))

print("Critical Value (Right-tailed):", round(critical_value, 4))

# Perform the hypothesis test

if t_score > critical_value:

    print("Reject the null hypothesis")

else:

    print("Fail to reject the null hypothesis")</code></pre>
        </div>
      </div>
      <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedPython === 'Copied' }" @click="copyCode('python')">
        {{ copyCodeOrCopiedPython }}
      </button>
    </div>

    <!-- R -->
    <div v-if="result" class="python-r-codes">
      <span class="img-text"><img alt="Icon for R language" src="../../../../assets/images/icon-r-calc.png"> <h4 class="h6">Your Result in R:</h4></span>
      <p class="codes-info">
        The following R code should provide the  hypothesis testing using the input data you entered.
      </p>
      <div class="codes-container">
        <div class="codes">
          <!-- Yes --------------------------------------------------------------------------------------------------- -->

          <div v-if="calculationData.answer === 'Yes' && calculationData.size >= 30">
            <!-- two tails -->
            <pre
              v-if="calculationData.method === 'readyData' && calculationData.selectedOperator === '='"
              class="language-r"
            ><code id="r">
# Null hypothesis: mean = ${{ calculationData.nullHypothesis }}

null_mean &lt;- {{ calculationData.nullHypothesis }}

# Population standard deviation: σ = ${{ calculationData.standardDeviation }}

pop_std_dev &lt;- {{ calculationData.standardDeviation }}

# Sample size

n &lt;- {{ calculationData.size }}

# Known sample mean

sample_mean &lt;- {{ calculationData.mean }}

# Calculate the standard error of the mean

se &lt;- pop_std_dev / sqrt(n)

# Calculate the z-score

z_score &lt;- (sample_mean - null_mean) / se

# Set the significance level (α)

significance_level &lt;- {{ calculationData.significanceLevel }}

# Determine the critical value

critical_value &lt;- qnorm(1 - significance_level/2)

# Print the z-score and critical value

cat("Z-Score (Test Statistic):", z_score, "\n")

cat("Critical Value:", critical_value, "\n")

# Perform the two-tailed hypothesis test

if (abs(z_score) > critical_value) {

  cat("Reject the null hypothesis\n")

} else {

  cat("Fail to reject the null hypothesis\n")

}
</code></pre>
            <pre
              v-if="calculationData.method === 'rawData' && calculationData.selectedOperator === '='"
              class="language-r"
            ><code id="r">
# Given dataset

dataset &lt;- c(<span class="no-wrap">{{ inputToNumbers[1] }}</span>)

# Null hypothesis: mean = ${{ calculationData.nullHypothesis }}

null_mean &lt;- {{ calculationData.nullHypothesis }}

# Population standard deviation: σ = ${{ calculationData.standardDeviation }}

pop_std_dev &lt;- {{ calculationData.standardDeviation }}

# Sample size

n &lt;- length(dataset)

# Sample mean

sample_mean &lt;- mean(dataset)

# Calculate the standard error of the mean

se &lt;- pop_std_dev / sqrt(n)

# Calculate the z-score

z_score &lt;- (sample_mean - null_mean) / se

# Set the significance level (α)

significance_level &lt;- {{ calculationData.significanceLevel }}

# Determine the critical value

critical_value &lt;- qnorm(1 - significance_level/2)

# Print the z-score and critical value

cat("Z-Score (Test Statistic):", z_score, "\n")

cat("Critical Value:", critical_value, "\n")

# Perform the two-tailed hypothesis test

if (abs(z_score) > critical_value) {

  cat("Reject the null hypothesis\n")

} else {

  cat("Fail to reject the null hypothesis\n")

}
</code></pre>

            <!-- left tail -->
            <pre
              v-if="calculationData.method === 'readyData' && calculationData.selectedOperator === '≥'"
              class="language-r"
            ><code id="r">
# Null hypothesis: mean = ${{ calculationData.nullHypothesis }}

null_mean &lt;- {{ calculationData.nullHypothesis }}

# Population standard deviation: σ = ${{ calculationData.standardDeviation }}

pop_std_dev &lt;- {{ calculationData.standardDeviation }}

# Sample size

n &lt;- {{ calculationData.size }}

# Known sample mean

sample_mean &lt;- {{ calculationData.mean }}

# Calculate the standard error of the mean

se &lt;- pop_std_dev / sqrt(n)

# Calculate the z-score

z_score &lt;- (sample_mean - null_mean) / se

# Set the significance level (α)

significance_level &lt;- {{ calculationData.significanceLevel }}

# Determine the critical value

critical_value &lt;- qnorm(significance_level)

# Print the z-score and critical value

cat("Z-Score (Test Statistic):", z_score, "\n")

cat("Critical Value:", critical_value, "\n")

# Perform the left-tailed hypothesis test

if (z_score &lt; critical_value) {

  cat("Reject the null hypothesis\n")

} else {

  cat("Fail to reject the null hypothesis\n")

}
</code></pre>
            <pre
              v-if="calculationData.method === 'rawData' && calculationData.selectedOperator === '≥'"
              class="language-r"
            ><code id="r">
# Given dataset

dataset &lt;- c(<span class="no-wrap">{{ inputToNumbers[1] }}</span>)

# Null hypothesis: mean = ${{ calculationData.nullHypothesis }}

null_mean &lt;- {{ calculationData.nullHypothesis }}

# Population standard deviation: σ = ${{ calculationData.standardDeviation }}

pop_std_dev &lt;- {{ calculationData.standardDeviation }}

# Sample size

n &lt;- length(dataset)

# Sample mean

sample_mean &lt;- mean(dataset)

# Calculate the standard error of the mean

se &lt;- pop_std_dev / sqrt(n)

# Calculate the z-score

z_score &lt;- (sample_mean - null_mean) / se

# Set the significance level (α)

significance_level &lt;- {{ calculationData.significanceLevel }}

# Determine the critical value

critical_value &lt;- qnorm(significance_level)

# Print the z-score and critical value

cat("Z-Score (Test Statistic):", z_score, "\n")

cat("Critical Value:", critical_value, "\n")

# Perform the left-tailed hypothesis test

if (z_score &lt; critical_value) {

  cat("Reject the null hypothesis\n")

} else {

  cat("Fail to reject the null hypothesis\n")

}
</code></pre>

            <!-- right tail -->
            <pre
              v-if="calculationData.method === 'readyData' && calculationData.selectedOperator === '≤'"
              class="language-r"
            ><code id="r">
# Null hypothesis: mean = ${{ calculationData.nullHypothesis }}

null_mean &lt;- {{ calculationData.nullHypothesis }}

# Population standard deviation: σ = ${{ calculationData.standardDeviation }}

pop_std_dev &lt;- {{ calculationData.standardDeviation }}

# Sample size

n &lt;- {{ calculationData.size }}

# Known sample mean

sample_mean &lt;- {{ calculationData.mean }}

# Calculate the standard error of the mean

se &lt;- pop_std_dev / sqrt(n)

# Calculate the z-score

z_score &lt;- (sample_mean - null_mean) / se

# Set the significance level (α)

significance_level &lt;- {{ calculationData.significanceLevel }}

# Determine the critical value

critical_value &lt;- qnorm(1 - significance_level)

# Print the z-score and critical value

cat("Z-Score (Test Statistic):", z_score, "\n")

cat("Critical Value:", critical_value, "\n")

# Perform the right-tailed hypothesis test

if (z_score > critical_value) {

  cat("Reject the null hypothesis\n")

} else {

  cat("Fail to reject the null hypothesis\n")

}
              </code>
            </pre>
            <pre
              v-if="calculationData.method === 'rawData' && calculationData.selectedOperator === '≤'"
              class="language-r"
            ><code id="r">
# Given dataset

dataset &lt;- c(<span class="no-wrap">{{ inputToNumbers[1] }}</span>)

# Null hypothesis: mean = ${{ calculationData.nullHypothesis }}

null_mean &lt;- {{ calculationData.nullHypothesis }}

# Population standard deviation: σ = ${{ calculationData.standardDeviation }}

pop_std_dev &lt;- {{ calculationData.standardDeviation }}

# Sample size

n &lt;- length(dataset)

# Sample mean

sample_mean &lt;- mean(dataset)

# Calculate the standard error of the mean

se &lt;- pop_std_dev / sqrt(n)

# Calculate the z-score

z_score &lt;- (sample_mean - null_mean) / se

# Set the significance level (α)

significance_level &lt;- {{ calculationData.significanceLevel }}

# Determine the critical value

critical_value &lt;- qnorm(1 - significance_level)

# Print the z-score and critical value

cat("Z-Score (Test Statistic):", z_score, "\n")

cat("Critical Value:", critical_value, "\n")

# Perform the right-tailed hypothesis test

if (z_score > critical_value) {

  cat("Reject the null hypothesis\n")

} else {

  cat("Fail to reject the null hypothesis\n")

}
</code></pre>
          </div>

          <div v-else-if="calculationData.answer === 'Yes' && calculationData.size < 30">
            <!-- two tails -->
            <pre
              v-if="calculationData.method === 'readyData' && calculationData.selectedOperator === '='"
              class="language-r"
            ><code id="r">
# Null hypothesis mean

null_mean &lt;- {{ calculationData.nullHypothesis }}

# Known population standard deviation

population_std_dev &lt;- {{ calculationData.standardDeviation }}

# Sample size

n &lt;- {{ calculationData.size }}

# Sample mean

sample_mean &lt;- {{ calculationData.mean }}

# Calculate the standard error of the mean

se &lt;- population_std_dev / sqrt(n)

# Calculate the t-statistic

t_statistic &lt;- (sample_mean - null_mean) / se

# Degrees of freedom

df &lt;- n - 1

# Set the significance level (α)

significance_level &lt;- {{ calculationData.significanceLevel }}

# Determine the critical values from the t-distribution

critical_value_left &lt;- qt(significance_level / 2, df)

critical_value_right &lt;- qt(1 - significance_level / 2, df)

# Print the results

cat("Sample Mean:", sample_mean, "\n")

cat("Null Hypothesis Mean:", null_mean, "\n")

cat("T-Statistic:", t_statistic, "\n")

cat("Critical Value (Left):", critical_value_left, "\n")

cat("Critical Value (Right):", critical_value_right, "\n")

# Make a decision based on the t-statistic and critical values

if (t_statistic &lt; critical_value_left || t_statistic > critical_value_right) {

  cat("Reject the null hypothesis\n")

} else {

  cat("Fail to reject the null hypothesis\n")

}
</code></pre>
            <pre
              v-if="calculationData.method === 'rawData' && calculationData.selectedOperator === '='"
              class="language-r"
            ><code id="r">
# Sample data

dataset &lt;- c(<span class="no-wrap">{{ inputToNumbers[1] }}</span>)

# Null hypothesis mean

null_mean &lt;- {{ calculationData.nullHypothesis }}

# Known population standard deviation

population_std_dev &lt;- {{ calculationData.standardDeviation }}

# Sample size

n &lt;- length(data)

# Sample mean

sample_mean &lt;- mean(data)

# Calculate the standard error of the mean

se &lt;- population_std_dev / sqrt(n)

# Calculate the t-statistic

t_statistic &lt;- (sample_mean - null_mean) / se

# Degrees of freedom

df &lt;- n - 1

# Set the significance level (α)

significance_level &lt;- {{ calculationData.significanceLevel }}

# Determine the critical values from the t-distribution

critical_value_left &lt;- qt(significance_level / 2, df)

critical_value_right &lt;- qt(1 - significance_level / 2, df)

# Print the results

cat("Sample Mean:", sample_mean, "\n")

cat("Null Hypothesis Mean:", null_mean, "\n")

cat("T-Statistic:", t_statistic, "\n")

cat("Critical Value (Left):", critical_value_left, "\n")

cat("Critical Value (Right):", critical_value_right, "\n")

# Make a decision based on the t-statistic and critical values

if (t_statistic &lt; critical_value_left || t_statistic > critical_value_right) {

  cat("Reject the null hypothesis\n")

} else {

  cat("Fail to reject the null hypothesis\n")

}
</code></pre>

            <!-- left tail -->
            <pre
              v-if="calculationData.method === 'readyData' && calculationData.selectedOperator === '≥'"
              class="language-r"
            ><code id="r">
# Null hypothesis mean

null_mean &lt;- {{ calculationData.nullHypothesis }}

# Known population standard deviation

population_std_dev &lt;- {{ calculationData.standardDeviation }}

# Sample size

n &lt;- {{ calculationData.size }}

# Sample mean

sample_mean &lt;- {{ calculationData.mean }}

# Calculate the standard error of the mean

se &lt;- population_std_dev / sqrt(n)

# Calculate the t-statistic

t_statistic &lt;- (sample_mean - null_mean) / se

# Degrees of freedom

df &lt;- n - 1

# Set the significance level (α)

significance_level &lt;- {{ calculationData.significanceLevel }}

# Determine the critical value from the t-distribution

critical_value &lt;- qt(significance_level, df)

# Print the results

cat("Sample Mean:", sample_mean, "\n")

cat("Null Hypothesis Mean:", null_mean, "\n")

cat("T-Statistic:", t_statistic, "\n")

cat("Critical Value (Left):", critical_value, "\n")

# Make a decision based on the t-statistic and critical value

if (t_statistic &lt; critical_value) {

  cat("Reject the null hypothesis\n")

} else {

  cat("Fail to reject the null hypothesis\n")

}
</code></pre>
            <pre
              v-if="calculationData.method === 'rawData' && calculationData.selectedOperator === '≥'"
              class="language-r"
            ><code id="r">
# Sample data

dataset &lt;- c(<span class="no-wrap">{{ inputToNumbers[1] }}</span>)

# Null hypothesis mean

null_mean &lt;- {{ calculationData.nullHypothesis }}

# Known population standard deviation

population_std_dev &lt;- {{ calculationData.standardDeviation }}

# Sample size

n &lt;- length(data)

# Sample mean

sample_mean &lt;- mean(data)

# Calculate the standard error of the mean

se &lt;- population_std_dev / sqrt(n)

# Calculate the t-statistic

t_statistic &lt;- (sample_mean - null_mean) / se

# Degrees of freedom

df &lt;- n - 1

# Set the significance level (α)

significance_level &lt;- {{ calculationData.significanceLevel }}

# Determine the critical value from the t-distribution

critical_value &lt;- qt(significance_level, df)

# Print the results

cat("Sample Mean:", sample_mean, "\n")

cat("Null Hypothesis Mean:", null_mean, "\n")

cat("T-Statistic:", t_statistic, "\n")

cat("Critical Value (Left):", critical_value, "\n")

# Make a decision based on the t-statistic and critical value

if (t_statistic &lt; critical_value) {

  cat("Reject the null hypothesis\n")

} else {

  cat("Fail to reject the null hypothesis\n")

}
</code></pre>

            <!-- right tail -->
            <pre
              v-if="calculationData.method === 'readyData' && calculationData.selectedOperator === '≤'"
              class="language-r"
            ><code id="r">
# Null hypothesis mean

null_mean &lt;- {{ calculationData.nullHypothesis }}

# Known population standard deviation

population_std_dev &lt;- {{ calculationData.standardDeviation }}

# Sample size

n &lt;- {{ calculationData.size }}

# Sample mean

sample_mean &lt;- {{ calculationData.mean }}

# Calculate the standard error of the mean

se &lt;- population_std_dev / sqrt(n)

# Calculate the t-statistic

t_statistic &lt;- (sample_mean - null_mean) / se

# Degrees of freedom

df &lt;- n - 1

# Set the significance level (α)

significance_level &lt;- {{ calculationData.significanceLevel }}

# Determine the critical value from the t-distribution

critical_value &lt;- qt(1 - significance_level, df)

# Print the results

cat("Sample Mean:", sample_mean, "\n")

cat("Null Hypothesis Mean:", null_mean, "\n")

cat("T-Statistic:", t_statistic, "\n")

cat("Critical Value (Right):", critical_value, "\n")

# Make a decision based on the t-statistic and critical value

if (t_statistic > critical_value) {

  cat("Reject the null hypothesis\n")

} else {

  cat("Fail to reject the null hypothesis\n")

}
              </code></pre>
            <pre
              v-if="calculationData.method === 'rawData' && calculationData.selectedOperator === '≤'"
              class="language-r"
            ><code id="r">
# Sample data

dataset &lt;- c(<span class="no-wrap">{{ inputToNumbers[1] }}</span>)

# Null hypothesis mean

null_mean &lt;- {{ calculationData.nullHypothesis }}

# Known population standard deviation

population_std_dev &lt;- {{ calculationData.standardDeviation }}

# Sample size

n &lt;- length(data)

# Sample mean

sample_mean &lt;- mean(data)

# Calculate the standard error of the mean

se &lt;- population_std_dev / sqrt(n)

# Calculate the t-statistic

t_statistic &lt;- (sample_mean - null_mean) / se

# Degrees of freedom

df &lt;- n - 1

# Set the significance level (α)

significance_level &lt;- {{ calculationData.significanceLevel }}

# Determine the critical value from the t-distribution

critical_value &lt;- qt(1 - significance_level, df)

# Print the results

cat("Sample Mean:", sample_mean, "\n")

cat("Null Hypothesis Mean:", null_mean, "\n")

cat("T-Statistic:", t_statistic, "\n")

cat("Critical Value (Right):", critical_value, "\n")

# Make a decision based on the t-statistic and critical value

if (t_statistic > critical_value) {

  cat("Reject the null hypothesis\n")

} else {

  cat("Fail to reject the null hypothesis\n")

}
</code></pre>
          </div>

          <!-- No ------------------------------------------------------------------------------------------------------- -->

          <!-- two tails -->
          <pre
            v-if="calculationData.method === 'readyData' && calculationData.answer === 'No' && calculationData.selectedOperator === '='"
            class="language-r"
          ><code id="r">
# Null hypothesis: mean = ${{ calculationData.nullHypothesis }}

null_mean &lt;- {{ calculationData.nullHypothesis }}

# Sample size

n &lt;- {{ calculationData.size }}

# Sample mean

sample_mean &lt;- {{ calculationData.mean }}

# Sample standard deviation

sample_std_dev &lt;- {{ calculationData.standardDeviation }}

# Calculate the standard error of the mean

se &lt;- sample_std_dev / sqrt(n)

# Calculate the t-score

t_score &lt;- (sample_mean - null_mean) / se

# Set the significance level (α)

significance_level &lt;- {{ calculationData.significanceLevel }}

# Determine the critical value

critical_value &lt;- qt(1 - significance_level/2, df = n-1)

# Print the t-score and critical value

cat("T-Score (Test Statistic):", t_score, "\n")

cat("Critical Value:", critical_value, "\n")

# Perform the two-tailed hypothesis test

if (abs(t_score) > critical_value) {

  cat("Reject the null hypothesis\n")

} else {

  cat("Fail to reject the null hypothesis\n")

}
</code></pre>
          <pre
            v-if="calculationData.method === 'rawData' && calculationData.answer === 'No' && calculationData.selectedOperator === '='"
            class="language-r"
          ><code id="r">
# Given dataset

dataset &lt;- c(<span class="no-wrap">{{ inputToNumbers[1] }}</span>)

# Null hypothesis: mean = ${{ calculationData.nullHypothesis }}

null_mean &lt;- {{ calculationData.nullHypothesis }}

# Sample size

n &lt;- length(dataset)

# Sample mean

sample_mean &lt;- mean(dataset)

# Sample standard deviation

sample_std_dev &lt;- sd(dataset)

# Calculate the standard error of the mean

se &lt;- sample_std_dev / sqrt(n)

# Calculate the t-score

t_score &lt;- (sample_mean - null_mean) / se

# Set the significance level (α)

significance_level &lt;- {{ calculationData.significanceLevel }}

# Determine the critical value

critical_value &lt;- qt(1 - significance_level/2, df = n-1)

# Print the t-score and critical value

cat("T-Score (Test Statistic):", t_score, "\n")

cat("Critical Value:", critical_value, "\n")

# Perform the two-tailed hypothesis test

if (abs(t_score) > critical_value) {

  cat("Reject the null hypothesis\n")

} else {

  cat("Fail to reject the null hypothesis\n")

}
</code></pre>

          <!-- left tail -->
          <pre
            v-if="calculationData.method === 'readyData' && calculationData.answer === 'No' && calculationData.selectedOperator === '≥'"
            class="language-r"
          ><code id="r">
# Null hypothesis: mean = ${{ calculationData.nullHypothesis }}

null_mean &lt;- {{ calculationData.nullHypothesis }}

# Sample size

n &lt;- {{ calculationData.size }}

# Sample mean

sample_mean &lt;- {{ calculationData.mean }}

# Sample standard deviation

sample_std_dev &lt;- {{ calculationData.standardDeviation }}

# Calculate the standard error of the mean

se &lt;- sample_std_dev / sqrt(n)

# Calculate the t-score

t_score &lt;- (sample_mean - null_mean) / se

# Set the significance level (α)

significance_level &lt;- {{ calculationData.significanceLevel }}

# Determine the critical value

critical_value &lt;- qt(significance_level, df = n-1)

# Print the t-score and critical value

cat("T-Score (Test Statistic):", t_score, "\n")

cat("Critical Value:", critical_value, "\n")

# Perform the left-tailed hypothesis test

if (t_score &lt; critical_value) {

  cat("Reject the null hypothesis\n")

} else {

  cat("Fail to reject the null hypothesis\n")

}
</code></pre>
          <pre
            v-if="calculationData.method === 'rawData' && calculationData.answer === 'No' && calculationData.selectedOperator === '≥'"
            class="language-r"
          ><code id="r">
# Given dataset

dataset &lt;- c(<span class="no-wrap">{{ inputToNumbers[1] }}</span>)

# Null hypothesis: mean = ${{ calculationData.nullHypothesis }}

null_mean &lt;- {{ calculationData.nullHypothesis }}

# Sample size

n &lt;- length(dataset)

# Sample mean

sample_mean &lt;- mean(dataset)

# Sample standard deviation

sample_std_dev &lt;- sd(dataset)

# Calculate the standard error of the mean

se &lt;- sample_std_dev / sqrt(n)

# Calculate the t-score

t_score &lt;- (sample_mean - null_mean) / se

# Set the significance level (α)

significance_level &lt;- {{ calculationData.significanceLevel }}

# Determine the critical value

critical_value &lt;- qt(significance_level, df = n-1)

# Print the t-score and critical value

cat("T-Score (Test Statistic):", t_score, "\n")

cat("Critical Value:", critical_value, "\n")

# Perform the left-tailed hypothesis test

if (t_score &lt; critical_value) {

  cat("Reject the null hypothesis\n")

} else {

  cat("Fail to reject the null hypothesis\n")

}
</code></pre>

          <!-- right tail -->
          <pre
            v-if="calculationData.method === 'readyData' && calculationData.answer === 'No' && calculationData.selectedOperator === '≤'"
            class="language-r"
          ><code id="r">
# Null hypothesis: mean = ${{ calculationData.nullHypothesis }}

null_mean &lt;- {{ calculationData.nullHypothesis }}

# Sample size

n &lt;- {{ calculationData.size }}

# Sample mean

sample_mean &lt;- {{ calculationData.mean }}

# Sample standard deviation

sample_std_dev &lt;- {{ calculationData.standardDeviation }}

# Calculate the standard error of the mean

se &lt;- sample_std_dev / sqrt(n)

# Calculate the t-score

t_score &lt;- (sample_mean - null_mean) / se

# Set the significance level (α)

significance_level &lt;- {{ calculationData.significanceLevel }}

# Determine the critical value

critical_value &lt;- qt(1 - significance_level, df = n-1)

# Print the t-score and critical value

cat("T-Score (Test Statistic):", t_score, "\n")

cat("Critical Value:", critical_value, "\n")

# Perform the right-tailed hypothesis test

if (t_score > critical_value) {

  cat("Reject the null hypothesis\n")

} else {

  cat("Fail to reject the null hypothesis\n")

}
</code></pre>
          <pre
            v-if="calculationData.method === 'rawData' && calculationData.answer === 'No' && calculationData.selectedOperator === '≤'"
            class="language-r"
          ><code id="r">
# Given dataset

dataset &lt;- c(<span class="no-wrap">{{ inputToNumbers[1] }}</span>)

# Null hypothesis: mean = ${{ calculationData.nullHypothesis }}

null_mean &lt;- {{ calculationData.nullHypothesis }}

# Sample size

n &lt;- length(dataset)

# Sample mean

sample_mean &lt;- mean(dataset)

# Sample standard deviation

sample_std_dev &lt;- sd(dataset)

# Calculate the standard error of the mean

se &lt;- sample_std_dev / sqrt(n)

# Calculate the t-score

t_score &lt;- (sample_mean - null_mean) / se

# Set the significance level (α)

significance_level &lt;- {{ calculationData.significanceLevel }}

# Determine the critical value

critical_value &lt;- qt(1 - significance_level, df = n-1)

# Print the t-score and critical value

cat("T-Score (Test Statistic):", t_score, "\n")

cat("Critical Value:", critical_value, "\n")

# Perform the right-tailed hypothesis test

if (t_score > critical_value) {

  cat("Reject the null hypothesis\n")

} else {

  cat("Fail to reject the null hypothesis\n")

}
</code></pre>
        </div>
      </div>
      <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedR === 'Copied' }" @click="copyCode('r')">
        {{ copyCodeOrCopiedR }}
      </button>
    </div>
  </div>
</template>

<script>
import calculatorsStepsMixin from '@itbpbg/lms-components/src/mixins/calculators/steps';

export default {
  mixins: [calculatorsStepsMixin],
  data() {
    return {
      lowerOrHigher: 'lower',
    };
  },
  computed: {
    significanceLevelDividedByTwo() {
      return this.calculationData.significanceLevel / 2;
    },
    currentTailData() {
      if (this.calculationData.selectedOperator === '=') {
        return ['two sided', this.calculationData.answer === 'Yes'
          ? '<p>To determine the critical values, we need to consult the <span class="bolded-texts-v-html">standard normal distribution table</span>. Since this is a <span class="bolded-texts-v-html">two-sided test</span>, we will divide the significance level (α) equally between the two tails. The critical value from the <span class="bolded-texts-v-html">standard normal distribution</span> table for probability is:</p>'
          : `<p>To determine the critical values, we need to consult the <span class="bolded-texts-v-html">Student’s T-table</span>. Since this is a <span class="bolded-texts-v-html">two-sided test</span>, we will divide the significance level (α) equally between the two tails. The critical value from the <span class="bolded-texts-v-html">Student’s T table</span> for probability (p) = 1 – ${this.calculationData.significanceLevel}/2 and (${this.calculationData.size}-1) degrees of freedom:</p>`];
      }

      if (this.calculationData.selectedOperator === '≥') {
        return ['left tailed', this.calculationData.answer === 'Yes'
          ? `<p>To determine the critical values, we need to consult the <span class="bolded-texts-v-html">standard normal distribution table</span>. Since this is a <span class="bolded-texts-v-html">one-sided left-tailed test</span>, we will allocate the significance level (α) to the left tail of the distribution. The critical value from the <span class="bolded-texts-v-html">standard normal distribution</span> table for probability (p) = 1 – ${this.calculationData.significanceLevel}:</p>`
          : `<p>To determine the critical values, we need to consult the <span class="bolded-texts-v-html">Student’s T-table</span>. Since this is a <span class="bolded-texts-v-html">one-sided left-tailed test</span>, we will allocate the significance level (α) to the left tail of the distribution. The critical value from the <span class="bolded-texts-v-html">Student’s T table</span> for probability (p) = 1 – ${this.calculationData.significanceLevel} and (${this.calculationData.size}-1) degrees of freedom:</p>`];
      }

      return ['right tailed', this.calculationData.answer === 'Yes'
        ? `<p>To determine the critical values, we need to consult the <span class="bolded-texts-v-html">standard normal distribution table</span>. Since this is a <span class="bolded-texts-v-html">one-sided right-tailed test</span>, we will allocate the significance level (α) to the right tail of the distribution. The critical value from the <span class="bolded-texts-v-html">standard normal distribution</span> table for probability (p) = 1 – ${this.calculationData.significanceLevel}:</p>`
        : `<p>To determine the critical values, we need to consult the <span class="bolded-texts-v-html">Student’s T-table</span>. Since this is a <span class="bolded-texts-v-html">one-sided right-tailed test</span>, we will allocate the significance level (α) to the right tail of the distribution. The critical value from the <span class="bolded-texts-v-html">Student’s T table</span> for probability (p) = 1 – ${this.calculationData.significanceLevel} and (${this.calculationData.size}-1) degrees of freedom:</p>`];
    },
    currentStatistic() {
      return this.calculationData.answer === 'Yes' && this.calculationData.size >= 30 ? ['z', 'Z', 'σ'] : ['t', 'T', 's'];
    },
    pValueSignificanceLevelComparison() {
      if (this.calculationData.pValue <= this.calculationData.significanceLevel) {
        return '≤';
      }

      return '>';
    },
    inputToNumbers() {
      const currentArray = this.calculationData.rawDataMeanArray.map((str) => Number(str));
      return [currentArray, currentArray.join(', ')];
    },
  },
  methods: {
    testStatisticCriticalValueComparison() {
      const currentScore = this.calculationData.selectedOperator === '≥' ? -Math.abs(this.calculationData.currentScore) : this.calculationData.currentScore;

      if (this.calculationData.selectedOperator === '='
        && (this.calculationData.testStatistic <= -Math.abs(this.calculationData.currentScore || this.calculationData.testStatistic >= Math.abs(currentScore))
          || this.calculationData.testStatistic >= currentScore)) {

        if (this.calculationData.testStatistic >= currentScore) {
          this.lowerOrHigher = 'higher';
        } else {
          this.lowerOrHigher = 'lower';
        }

        return true;
      }

      if (this.calculationData.selectedOperator === '≤' && this.calculationData.testStatistic >= currentScore) {
        this.lowerOrHigher = 'higher';

        return true;
      }

      if (this.calculationData.selectedOperator === '≥' && this.calculationData.testStatistic <= currentScore) {
        this.lowerOrHigher = 'lower';

        return true;
      }

      return false;
    },
  },
};
</script>

<style lang="scss" scoped>
.calculation-steps {
  p,
  .no-margin.no-margin {
    margin: 0;
  }

  .top-bottom-margins.top-bottom-margins {
    margin: var(--spacing-16) 0;
  }

  ::v-deep .bolded-texts-v-html {
    font-weight: 600;
  }

  .python-r-codes.python {
    margin-top: var(--spacing-80);
  }

  ol {
    li {
      margin-top: var(--spacing-32);

      &:nth-of-type(2) {
        margin-bottom: var(--spacing-16);
      }

      &:nth-of-type(4) {
        margin: var(--spacing-16) 0;
      }

      &:nth-of-type(5) {
        margin-top: var(--spacing-16);
      }

      &:nth-of-type(6) {
        margin: var(--spacing-16) 0;
      }
    }
  }

  ul {
    li {
      &.p-value-approach {
        margin: var(--spacing-16) 0 0;
      }
    }
  }

  .ul-big {
    margin-bottom: var(--spacing-16);
  }
}

@include bp($bp-mobile) {
  div .calculation-steps {
    padding-top: var(--spacing-40);

    .h4 {
      text-align: left;
    }

    .top-margin-mobile.top-margin-mobile {
      margin-top: var(--spacing-10);
    }
  }
}

@import '../../../../assets/sass/ds/calculators/calculation-steps.scss';
</style>
